<template>
  <div class="panel signup-panel">
    <div class="header-content">
      <a href="https://quickreceipts.co"><img src="@/assets/img/logo-full.png" alt="" /></a>
    </div>
    <div class="content-wrapper">
      <div class="static-info-container">
        <p class="static-info-intro-text">Get More Out Of Your Receipts</p>
        <div class="static-info-list">
          <div class="static-info-list-item">
            <div class="static-info-list-item-icon">
              <img src="@/assets/img/intro/lower-costs.png" />
            </div>
            <div class="static-info-list-item-content">
              <div class="static-info-list-item-label">
                Lower Your Costs
              </div>
              <div class="static-info-list-item-description">
                Immediately save money on receipt paper and ink. Don't waste anymore time replacing empty or outdated printers, and reduce checkout time at the register with a faster receipt option.
              </div>
            </div>
          </div>
          <div class="static-info-list-item">
            <div class="static-info-list-item-icon">
              <img src="@/assets/img/intro/privacy.png" />
            </div>
            <div class="static-info-list-item-content">
              <div class="static-info-list-item-label">
                Privacy Customers Want
              </div>
              <div class="static-info-list-item-description">
                Consumers are more concerned than ever about privacy. Establish trust in your brand by offering a digital receipts option that doesn’t require the collection of emails or phone numbers.
              </div>
            </div>
          </div>
          <div class="static-info-list-item">
            <div class="static-info-list-item-icon">
              <img src="@/assets/img/intro/reduce.png" />
            </div>
            <div class="static-info-list-item-content">
              <div class="static-info-list-item-label">
                Reduce Environmental Waste
              </div>
              <div class="static-info-list-item-description">
                For every 25,000 receipts generated with our API, we’ll donate a tree to our partners, the Arbor Day Foundation —the largest non-profit conservation and education organization dedicated to planting trees.
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="content-container">
        <card class="signup-panel-content">
          <p class="text-center"><img src="@/assets/img/logo-icon.png" alt="logo-icon" /></p>
          <p class="text-center"><strong>Developer Sign Up</strong></p>
          <form @submit.prevent>
            <div class="form-group">
              <fg-input type="text"
                        placeholder="Name"
                        v-model="form.name">
                <span class="error text-danger" v-show="form.error.name">Name is missing...</span>
              </fg-input>
            </div>
            <div class="form-group">
              <fg-input type="email"
                        placeholder="Email"
                        v-model="form.email">
                <span class="error text-danger" v-show="form.error.email">Email is not valid.</span>
              </fg-input>
            </div>
            <div class="form-group">
              <password
                placeholder="Password"
                :secureLength="passwordMinLength"
                @score="showScore"
                v-model="form.password"
                defaultClass="form-control"
                :badge="false"
              />
              <template v-if="form.error.password">
                <span class="error text-danger" v-if="form.password.length < 8">Password must be 8 characters</span>
                <span class="error text-danger" v-else="passwordScore < 2">Password must be stronger</span>
              </template>
            </div>
            <div class="form-group">
              <fg-input type="password"
                        placeholder="Confirm Password"
                        v-model="form.confirm_password">
                <span class="error text-danger" v-show="form.confirm_password && form.error.confirm_password">Password does not match.</span>
              </fg-input>
            </div>
            <div class="form-group">
              <fg-input type="text"
                        placeholder="Company (optional)"
                        v-model="form.company">
              </fg-input>
            </div>

            <div class="form-group terms-text">
              <span>By signing up you agree to <a href="https://quickreceipts.co/terms" target="_blank">the terms of use</a></span>
            </div>

            <p-button nativeType="submit" type="success" block @click.native="onSubmit" :disabled="hasError() || loading || processing" :loading="loading || processing">
              SIGN UP
            </p-button>
            <p v-show="error" class="error text-danger">{{error}}</p>

            <hr/>

            <p-button class="btn-with-github" block @click.native="onSubmitGithub" :disabled="loading || processing">
              <img src="@/assets/img/providers/github.png" alt="" /> Sign up with GitHub
            </p-button>
            <p-button class="btn-with-google" block @click.native="onSubmitGoogle" :disabled="loading || processing">
              <img src="@/assets/img/providers/google.png" alt="" /> Sign up with Google
            </p-button>
          </form>
          <div class="footer-content">
            <span>Already a member?</span>&nbsp;<strong><router-link to="/login">Sign in</router-link></strong>
          </div>
        </card>
        <div class="copyright">&copy; 2019 <strong>QUICKRECEIPTS</strong> ALL RIGHTS RESERVED</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Password from 'vue-password-strength-meter'

export default {
  components: {
    Password
  },
  data() {
    return {
      passwordMinLength: 8,
      passwordScore: null,
      form: {
        name: null,
        email: null,
        password: null,
        confirm_password: null,
        company: null,
        error: {
          name: false,
          email: false,
          password: false,
          confirm_password: false
        },
      },
      processing: false
    }
  },
  watch: {
    'form.name': function () {
      this.form.error.name = !this.form.name
    },
    'form.email': function () {
      this.form.error.email = !this.form.email || !this.form.email.validateEmail()
    },
    'form.password': function() {
      this.form.error.password = this.passwordScore < 2 || this.form.password.length < 8
    },
    'form.confirm_password': function() {
      this.form.error.confirm_password = this.form.password !== this.form.confirm_password
    }
  },
  computed: {
    ...mapGetters([
      'error',
      'loading',
    ])
  },
  methods: {
    onSubmit(e) {
      e.preventDefault()
      if (!this.hasError()) {
        const {confirm_password, error, ...payload} = this.form
        this.processing = true
        this.$store.dispatch('userSignUpWithEmail', payload).finally(() => this.processing = false)
      }
    },
    validate() {
      this.form.error.name = !this.form.name
      this.form.error.email = !this.form.email || !this.form.email.validateEmail()
      this.form.error.password = this.passwordScore < 2
      this.form.error.confirm_password = this.form.password !== this.form.confirm_password
    },
    hasError() {
      return Object.keys(this.form.error).some((key) => this.form.error[key] || !this.form[key])
    },
    showScore (score) {
      this.passwordScore = score
    },
    onSubmitGithub() {
      this.$store.dispatch('userSignUpWithGithub')
    },
    onSubmitGoogle() {
      this.$store.dispatch('userSignUpWithGoogle')
    }
  }
}
</script>
<style lang="scss">
.signup-panel {
  margin: 15px 15px;
  .header-content {
    margin-bottom: 45px;
    img {
      width: 260px;
    }
  }
  .content-wrapper {
    display: flex;
  }
  .static-info-container {
    color: #000;
    max-width: 600px;
    margin: 15px 65px 15px 0;
    .static-info-intro-text {
      font-size: 34px;
      font-weight: 900;
    }
    .static-info-list {
      display: flex;
      flex-direction: column;
      .static-info-list-item {
        margin: 10px 0;
        display: flex;
        align-items: center;
        .static-info-list-item-icon {
          flex: 0 0 60px;
          margin-right: 20px;
          text-align: center;
          img {
            width: 52px;
          }
        }
        .static-info-list-item-label {
          font-size: 22px;
          margin-bottom: 5px;
        }
        .static-info-list-item-description {
          font-size: 16px;
          line-height: 1.3;
          color: #919191;
        }
      }
    }
  }
  .signup-panel-content {
    min-width: 360px;
    padding: 20px 20px 0;
    box-shadow: 0 2px 6px rgba(204, 197, 185, 0.5);
    form {
      max-width: 300px;
      margin: 30px auto;
    }
    .btn {
      text-transform: initial;
      height: 40px;
      box-shadow: 0 0 4px 2px #dedede;
      // display: flex;
      // justify-content: space-evenly;
      img {
        margin-right: 25px;
      }
    }
  }
  .footer-content {
    text-align: center;
    margin-bottom: 25px;
  }
  .terms-text {
    font-size: 0.8em;
    text-align: center;
    color: #777;
  }
  .Password__badge {
    height: 20px;
    text-align: center;
  }
  .btn-with-github {
    background: linear-gradient(to bottom, #444, #000);
    border: 0 none;
    transition: none;
    &:hover {
      background: #000;
    }
  }
  .btn-with-google {
    background: linear-gradient(to bottom, #fff, #f8f8f8);
    color: #000;
    border: 0 none;
    transition: none;
    &:hover {
      background: #ececec;
    }
  }
  .copyright {
    font-size: 10px;
    text-align: center;
  }
  @media (max-width: 900px) {
    .header-content {
      text-align: center;
    }
    .content-wrapper {
      flex-direction: column;
      align-items: center;
      .static-info-container {
        order: 2;
        margin-top: 65px;
        margin-left: 15px;
        margin-right: 15px;
      }
    }
  }
  @media (max-width: 600px) {
    .static-info-container {
      margin-left: 15px;
      margin-right: 15px;
      .static-info-intro-text {
        text-align: center;
      }
      .static-info-list {
        .static-info-list-item {
          flex-direction: column;
          .static-info-list-item-icon {
            flex: 0 0 auto;
          }
          .static-info-list-item-content {
            text-align: center;
            margin: 15px 5px 10px;
          }
        }
      }
    }
    .signup-panel-content {
      min-width: auto;
    }
  }
}
</style>